import { Link } from "react-router-dom";
import { usePortalRedirect, useMySubscription } from "../../../lib/api/subscription";
import type { SubscriptionRow } from "../../../lib/types";

// /settings/billing — current plan + "Manage billing" CTA. The CTA POSTs
// /api/stripe/portal to mint a Customer Portal session URL and redirects.
//
// No frontend feature flag — the billing Worker owns the live-mode kill
// switch (Wrangler secret rotation sk_test_... → sk_live_...). Users
// without an active subscription see "See plans" instead of "Manage
// billing"; the API returns 404 in that case and we never call it.
export function BillingSettingsRoute() {
	const sub = useMySubscription();
	const portal = usePortalRedirect();

	const row = sub.data;
	const hasActive = row && row.status !== "none" && !!row.stripeCustomerId;

	return (
		<div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
			<header>
				<Link
					to="/settings"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Settings
				</Link>
				<h1 className="mt-1 text-2xl font-bold">Billing</h1>
			</header>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">Current plan</h2>
				{sub.isLoading && (
					<p className="text-sm text-[var(--color-muted)]">Loading…</p>
				)}
				{sub.isError && (
					<p className="text-sm text-[var(--color-danger)]">
						Could not load subscription. {sub.error.message}
					</p>
				)}
				{row && <PlanSummary row={row} />}
			</section>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">Manage</h2>
				{hasActive ? (
					<button
						type="button"
						disabled={portal.isPending}
						onClick={() => portal.mutate()}
						className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:opacity-60"
					>
						{portal.isPending ? "Opening portal…" : "Manage billing"}
					</button>
				) : (
					<Link
						to="/upgrade"
						className="inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
					>
						See plans
					</Link>
				)}
				{portal.isError && (
					<p className="text-xs text-[var(--color-danger)]">
						{portal.error.message}
					</p>
				)}
			</section>
		</div>
	);
}

interface PlanSummaryProps {
	row: SubscriptionRow;
}

function PlanSummary({ row }: PlanSummaryProps) {
	if (row.status === "none") {
		return (
			<p className="text-sm">
				You're on the <span className="font-semibold">Free</span> plan.
			</p>
		);
	}
	const cycle = row.billingCycle === "annual" ? "Annual" : "Monthly";
	const renews = row.currentPeriodEnd
		? new Date(row.currentPeriodEnd).toLocaleDateString()
		: null;
	return (
		<div className="space-y-1 text-sm">
			<p>
				Status: <span className="font-semibold capitalize">{row.status}</span>
				{" · "}
				<span className="font-semibold">{cycle}</span>
			</p>
			{renews && (
				<p className="text-[var(--color-muted)]">
					{row.cancelAtPeriodEnd ? "Ends" : "Renews"} on {renews}
				</p>
			)}
		</div>
	);
}
